import externalDayjs, { Dayjs } from 'dayjs';
import dayJsUtc from 'dayjs/plugin/utc';
import dayjsTimezone from 'dayjs/plugin/timezone';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { ENV_CONFIG } from 'config/env';
import { InsightViewRelativeTimeCategory, InsightViewRelativeTimeType } from 'constant/ViewConstant';

externalDayjs.extend(dayJsUtc);
externalDayjs.extend(dayjsTimezone);
externalDayjs.extend(weekOfYear);

// set default timezone
externalDayjs.tz.setDefault(ENV_CONFIG.config.TIME_ZONE);

export const dayjs = externalDayjs;

export function dayjsWithUTC(date?: string | number | Date | Dayjs, format?: string): Dayjs {
  return dayjs(date, format).utc();
}

export function dayParseUTCFormat(date: string, format?: string): string {
  const zoned = externalDayjs.utc(date).tz(getAppTimeZone());
  // dayjs has no token that yields a real zone abbreviation (its `z` needs a
  // plugin and still only prints "GMT+10"), so swap lowercase z/zz/zzz for the
  // IANA short name (e.g. AEST/AEDT, DST-aware) as an escaped literal before
  // formatting. Uppercase Z/ZZ (numeric offset) is left to dayjs.
  const resolvedFormat =
    format && /z/.test(format)
      ? format.replace(/z{1,3}/g, `[${getZoneAbbreviation(zoned.toDate(), getAppTimeZone())}]`)
      : format;
  return zoned.format(resolvedFormat);
}

/**
 * The single source of truth for the app display timezone.
 * Today this is the build-time TIME_ZONE config; centralising it here means a
 * future move to a per-tenant timezone only needs to change this accessor.
 */
export function getAppTimeZone(): string {
  return ENV_CONFIG.config.TIME_ZONE;
}

/**
 * Resolve a real timezone abbreviation (e.g. AEST/AEDT) for an instant.
 * Abbreviations are locale-data dependent — "AEST" only renders under en-AU,
 * while en-US/en give "GMT+10". Try locales that carry a real abbreviation and
 * fall back to the offset short name.
 */
function getZoneAbbreviation(date: Date, timeZone: string): string {
  for (const locale of ['en-AU', 'en-US', 'en-GB', 'en']) {
    const value = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;
    if (value && !/^(GMT|UTC)/i.test(value)) return value;
  }
  return (
    new Intl.DateTimeFormat('en', { timeZone, timeZoneName: 'short' })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  );
}

/**
 * Strip timezone tokens (offset `Z`/`ZZ` and name `z`/`zz`/`zzz`, plus the
 * literals/parentheses wrapping them) from a dayjs format string. MUI X v6
 * picker fields render the format as editable sections and don't support these
 * tokens, so the editable input would show them literally (e.g. "(UTCZ)" or
 * "(z)"). Pickers therefore display only the wall-clock; the zone is still
 * shown in read-only views (table), which format via dayjs directly.
 */
export function stripTimeZoneFromFormat(format: string): string {
  return format
    .replace(/\[[^\]]*\]/g, '') // drop escaped literals, e.g. [(UTC] and [)]
    .replace(/[zZ]{1,4}/g, '') // drop tz tokens: Z/ZZ (offset) and z/zz/zzz (name)
    .replace(/\(\s*\)/g, '') // drop empty () left behind, e.g. from "(z)"
    .replace(/\s{2,}/g, ' ')
    .trim();
}

enum DateOptions {
  DAY = 'Day',
  WEEK = 'Week',
  WEEKS = 'Weeks',
  MONTH = 'Month',
  MONTHS = 'Months',
  YEAR = 'Year',
  YEARS = 'Years',
}

export function getJulianDate(date: Dayjs): number {
  const year = date.year();
  const month = date.month() + 1;
  const day = date.date();

  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;

  const julianDate =
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045;

  return julianDate;
}

export function getJulianDateBasedOnDayType(jDate: number, option: DateOptions | string, decimal: number) {
  let julianDate = jDate;
  switch (option.toLowerCase()) {
    case DateOptions.WEEK.toLowerCase():
    case DateOptions.WEEKS.toLowerCase():
      julianDate = julianDate / 7;
      break;
    case DateOptions.MONTH.toLowerCase():
    case DateOptions.MONTHS.toLowerCase():
      julianDate = (julianDate * 12) / 365;
      break;
    case DateOptions.YEAR.toLowerCase():
    case DateOptions.YEARS.toLowerCase():
      julianDate = julianDate / 365;
      break;
  }
  return Number(julianDate.toFixed(decimal));
}

export function getInsightViewRelativeTimeDescription(number: number, relativeTimeType: string, timeCategory: string) {
  const timeDescriptions = {
    [InsightViewRelativeTimeCategory.YEAR_TIME]: {
      [InsightViewRelativeTimeType.CURRENT_TIME]: 'Current Year',
      [InsightViewRelativeTimeType.PAST_TIME]: number === 1 ? 'Last Year' : `${number} Years Ago`,
      [InsightViewRelativeTimeType.FUTURE_TIME]: number === 1 ? 'Next Year' : `${number} Years Ahead`,
    },
    [InsightViewRelativeTimeCategory.PERIOD_TIME]: {
      [InsightViewRelativeTimeType.CURRENT_TIME]: 'Current Period',
      [InsightViewRelativeTimeType.PAST_TIME]: number === 1 ? 'Last Period' : `${number} Periods Ago`,
      [InsightViewRelativeTimeType.FUTURE_TIME]: number === 1 ? 'Next Period' : `${number} Periods Ahead`,
    },
  };

  return timeDescriptions[timeCategory]?.[relativeTimeType] || '';
}
